import { forwardRef } from 'react'

import { GridLayout, useContainerWidth } from 'react-grid-layout'
import type { Layout } from 'react-grid-layout'
// react-grid-layout 本体は positioning のみを inline transform で行い、ハンドルの
// 見た目 (ホバー時のみ表示される se/s/e リサイズハンドル) はこの CSS が担う。
// design (`design/js/bg-data.js` / `hatsu-data.js`) が使う GridStack のリサイズ
// ハンドル見た目に相当する既定スタイルで、DS token 化対象の UI ではない
// (サードパーティ library 付属の operational affordance) ため raw import のまま使う。
import 'react-grid-layout/css/styles.css'

/**
 * 同時同量ダッシュボード (需要 `AreaCodeGrid` / 発電 `BgGrid`) 共通のカード
 * グリッド (ドラッグ並び替え + グリッド単位リサイズ) の見た目・配線を 1 箇所に
 * 集約する shared component。個々のカードの内容・データ取得は呼び出し側の責務。
 *
 * grid 単位は design プロトタイプ (`design/js/bg-data.js` の GridStack 設定:
 * `column:12, cellHeight:70, margin:6, resizable:{handles:'se, s, e'}`) に揃える。
 */
const GRID_COLS = 12
const GRID_ROW_HEIGHT = 70
const GRID_MARGIN: readonly [number, number] = [6, 6]
const RESIZE_HANDLES = ['se', 's', 'e'] as const

/**
 * ドラッグ開始領域を絞る CSS class (= [[GridCard]] のみに閉じたローカル定数)。
 * `dragConfig.handle` セレクタとカード側の className の 2 箇所で同じ文字列を
 * 使う必要があるが、両方ともこのファイル内に閉じているので export しない
 * (= 複数ファイルに同じ生文字列が散らばって typo で静かに壊れる事故を防ぐ)。
 */
const DRAG_HANDLE_CLASS = 'grid-card-drag-handle'

type GridCardProps = React.HTMLAttributes<HTMLDivElement> & {
  children: React.ReactNode
}

/**
 * `CardGridLayout` の子として置く、カード 1 枚ぶんの薄いラッパ。
 *
 * react-grid-layout の `GridItem` は `React.cloneElement(child, {ref,
 * className, style})` で唯一の子要素へドラッグ/リサイズ位置を注入する。この
 * ラッパだけを forwardRef 化することで、カード本体 (`AreaCodeChart` /
 * `BgChart` 等) は react-grid-layout を一切意識しないプレーンな component の
 * ままでいられる (= grid 配線を触るたびにチャート component へ ref/style の
 * 穴を開けずに済む)。
 *
 * ドラッグは現状カード全体で発火する (= 旧実装にあった「ヘッダーのみ
 * draggableHandle」の限定は、このラッパ 1 個への集約と引き換えに撤去した。
 * カード内の Recharts ツールチップ・詳細リンク等は、ドラッグ判定の
 * `threshold`〈既定 3px〉未満のクリックでは発火しないため通常操作は維持される
 * が、意図的なドラッグはカード内どこから始めても成立する)。
 *
 * **残余 props を必ず `div` へ渡す**こと。react-grid-layout の GridItem は
 * `DraggableCore`(react-draggable) → `Resizable`(react-resizable) の順で
 * このラッパを包み、`DraggableCore.render()` は `onMouseDown` / `onMouseUp` /
 * `onTouchEnd` を **子要素の props として** cloneElement で注入し (DOM へ
 * addEventListener しない)、`Resizable.render()` はその残余 props を自分の子
 * (= このラッパ) へそのまま spread する。よってこのラッパが残余 props を捨てると
 * `onMouseDown` が DOM へ届かず、マウスでのドラッグが一切開始しない。リサイズは
 * ハンドル span 側に注入されるため動いてしまい、症状が「リサイズだけ効く」という
 * 非対称な形になる (型上も cloneElement の余剰 props は tsc で検出されない)。
 *
 * `display: 'grid'` を明示するのは、react-grid-layout が `style` に注入する
 * `width` / `height` / `position: absolute` (`setTransform`、セル寸法ぶんの
 * 実ピクセル値) を **この wrapper 自身が受け取るだけ** で、唯一の子である
 * カード本体 (`AreaCodeChart` / `BgChart`) の root には一切伝播しないため。
 * block 要素のままだと子は内容量ぶんの高さしか取らず、セルとの差が空白として
 * 残り、縦方向のグリッドリサイズが見た目に反映されない (= grid item は既定で
 * 両軸 stretch なので、唯一の子要素がこの div のセル高いっぱいに伸びる)。
 * カード本体側の overflow 指定 (発電 `hidden` / 需要 `visible`) は尊重し、
 * ここでは overflow を追加しない。
 */
export const GridCard = forwardRef<HTMLDivElement, GridCardProps>(
  function GridCard({ className, style, children, ...rest }, ref) {
    return (
      <div
        {...rest}
        className={[DRAG_HANDLE_CLASS, className].filter(Boolean).join(' ')}
        ref={ref}
        style={{ ...style, display: 'grid' }}
      >
        {children}
      </div>
    )
  },
)

type Props = {
  layout: Layout
  /** ドラッグ / リサイズ確定時 (mouseup) に最終レイアウトを渡す。 */
  onLayoutSettled: (layout: Layout) => void
  /** 各カードの内容。[[GridCard]] で 1 枚ずつ包み、key は `layout` の `i` (カード安定 ID) と一致させる。 */
  children: React.ReactNode
}

export const CardGridLayout = ({
  layout,
  onLayoutSettled,
  children,
}: Props): React.ReactElement => {
  const { width, containerRef } = useContainerWidth()

  return (
    <div
      // react-grid-layout の `useContainerWidth` は `containerRef:
      // RefObject<HTMLDivElement | null>` を返す。peerDeps は react >=16.3
      // (devDep は 18.3.1) なので React 19 固有の型ではなく、単に
      // `@types/react` の `ref` 受け口 (`LegacyRef<T>` が要求する
      // `RefObject<HTMLDivElement>`) と構造的に一致しないだけ (実体は同じ
      // RefObject オブジェクト) — TS の変性判定で弾かれるため型アサーションで
      // 橋渡しする。
      ref={containerRef as React.LegacyRef<HTMLDivElement>}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      <GridLayout
        width={width}
        dragConfig={{ handle: `.${DRAG_HANDLE_CLASS}` }}
        gridConfig={{
          cols: GRID_COLS,
          rowHeight: GRID_ROW_HEIGHT,
          margin: GRID_MARGIN,
        }}
        layout={layout}
        onDragStop={(nextLayout) => onLayoutSettled(nextLayout)}
        onResizeStop={(nextLayout) => onLayoutSettled(nextLayout)}
        resizeConfig={{ handles: RESIZE_HANDLES }}
      >
        {children}
      </GridLayout>
    </div>
  )
}
